import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {IMachineryGroup, MachineryGroupFilter, NO_GROUP_FILTER} from "../../../models/iMachineryGroup";
import {
    fetchAddMachineryGroup,
    fetchCreateMachineryGroupShare,
    fetchDeleteMachineryGroup,
    fetchGetMachineryGroups,
    fetchRevokeMachineryGroupShare,
    fetchSetMachineryGroupMembers,
    fetchUpdateMachineryGroup,
} from "./actions";

interface IMachineryGroupsState {
    groups: IMachineryGroup[];
    /** Фильтр таблицы по группе. null = все группы. */
    groupFilter: MachineryGroupFilter;
    isLoading: boolean;
    /** Идёт мутация (создание/переименование/удаление/состав) — блокирует кнопки диалога. */
    isSaving: boolean;
    errorMessage: string;
}

const initialState: IMachineryGroupsState = {
    groups: [],
    groupFilter: null,
    isLoading: false,
    isSaving: false,
    errorMessage: "",
};

/** Список групп всегда отсортирован по имени — как отдаёт бэкенд. */
const sortByName = (groups: IMachineryGroup[]) =>
    [...groups].sort((a, b) => a.name.localeCompare(b.name, "ru"));

export const MachineryGroupsSlice = createSlice({
    name: "machinery_groups",
    initialState,
    reducers: {
        setMachineryGroupFilter: (state, action: PayloadAction<MachineryGroupFilter>) => {
            state.groupFilter = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchGetMachineryGroups.pending, (state) => {
                state.isLoading = true;
                state.errorMessage = "";
            })
            .addCase(fetchGetMachineryGroups.fulfilled, (state, action) => {
                state.groups = action.payload;
                state.isLoading = false;
                // Выбранной группы больше нет (удалена в другой вкладке) —
                // фильтр обязан вернуться к «Все», иначе таблица пустая
                // и непонятно почему.
                //
                // NO_GROUP_FILTER под сброс НЕ попадает: это не ссылка на
                // группу, а самостоятельное состояние «машины без группы»,
                // осмысленное и при пустом справочнике. Иначе выбор молча
                // слетал бы на «Все» при каждом перечитывании списка —
                // например после сохранения состава группы.
                if (
                    state.groupFilter !== null &&
                    state.groupFilter !== NO_GROUP_FILTER &&
                    !action.payload.some((g) => g.id === state.groupFilter)
                ) {
                    state.groupFilter = null;
                }
            })
            .addCase(fetchGetMachineryGroups.rejected, (state, action) => {
                state.isLoading = false;
                state.errorMessage = action.payload ?? "Не удалось загрузить группы";
            })

            .addCase(fetchAddMachineryGroup.fulfilled, (state, action) => {
                state.groups = sortByName([...state.groups, action.payload]);
                state.isSaving = false;
            })

            .addCase(fetchUpdateMachineryGroup.fulfilled, (state, action) => {
                state.groups = sortByName(
                    state.groups.map((g) => (g.id === action.payload.id ? action.payload : g)),
                );
                state.isSaving = false;
            })

            .addCase(fetchDeleteMachineryGroup.fulfilled, (state, action) => {
                state.groups = state.groups.filter((g) => g.id !== action.payload);
                if (state.groupFilter === action.payload) state.groupFilter = null;
                state.isSaving = false;
            })

            .addCase(fetchSetMachineryGroupMembers.fulfilled, (state, action) => {
                state.groups = state.groups.map((g) =>
                    g.id === action.payload.id ? action.payload : g,
                );
                state.isSaving = false;
            })

            // Выпуск/перевыпуск/отзыв ссылки возвращают ту же группу с
            // обновлённым share_token — подменяем строку целиком.
            .addMatcher(
                (action) =>
                    action.type === fetchCreateMachineryGroupShare.fulfilled.type ||
                    action.type === fetchRevokeMachineryGroupShare.fulfilled.type,
                (state, action: PayloadAction<IMachineryGroup>) => {
                    state.groups = state.groups.map((g) =>
                        g.id === action.payload.id ? action.payload : g,
                    );
                    state.isSaving = false;
                },
            )

            // Общие pending/rejected для всех мутаций: у них одинаковая
            // реакция интерфейса — заблокировать кнопки и показать ошибку.
            .addMatcher(
                (action) =>
                    action.type.startsWith("machinery_groups/") &&
                    action.type.endsWith("/pending") &&
                    !action.type.startsWith("machinery_groups/get_all"),
                (state) => {
                    state.isSaving = true;
                    state.errorMessage = "";
                },
            )
            .addMatcher(
                (action) =>
                    action.type.startsWith("machinery_groups/") &&
                    action.type.endsWith("/rejected") &&
                    !action.type.startsWith("machinery_groups/get_all"),
                (state, action: PayloadAction<string | undefined>) => {
                    state.isSaving = false;
                    state.errorMessage = action.payload ?? "Не удалось сохранить группу";
                },
            );
    },
});

export const {setMachineryGroupFilter} = MachineryGroupsSlice.actions;
export default MachineryGroupsSlice.reducer;
